import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider as AntDivider } from "antd";
import CustomDomainPreview from "../../../../Static/images/custom-domain-preview.png";
import Button from "Components/UI/Button";
import Form from "Components/UI/Form/Form";
import { createSchema } from './customDomain.schema';
import { 
  CardSubTitle,
  CardTitle,
  DNSWrapper,
  Divider, 
  InfoWrapper, 
  InputDNSWrapper, 
  MainWrapperLeft, 
  MainWrapperRight,
  SvgCard,
} from "./styles";
import Field from "Components/UI/Field";
import { Icon } from "Components/UI";
import { Dispatch } from "redux";
import { MultiDomainPersonalizationStatusEnum } from "Data/interfaces/MultiDomain/IMultiDomainPersonalizationResponse";
import { useSelector } from "react-redux";
import { getDomain, getDomainLoading } from "Store/MultiDomain/MultiDomain.selector";
import { MultiDomainActions } from "Store/MultiDomain/MultiDomain.action";
import { ITenantPersonalizationUpdateData, IUpdateTenantPersonalizationRequest } from "Data/interfaces/MultiDomain/IUpdateMultiDomainPersonalization";
import ButtonsAction from "../ButtonsAction";
import { ICheckDNSRequest } from "Data/interfaces/MultiDomain/ICheckDNSRequest";

interface ICustomDomainTab {
  dispatch: Dispatch<any>;
  tenantFk?: number;
}

const CustomDomainTab: FC<ICustomDomainTab> = ({
  dispatch,
  tenantFk,
}) => {
  const { t } = useTranslation('personalization');
  const domain = useSelector(getDomain);
  const loadingDomain = useSelector(getDomainLoading);

  const schema = useMemo(() => createSchema(t), [t]);

  const [statusDomain, setStatusDomain] = useState<MultiDomainPersonalizationStatusEnum>(domain.Status);
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [isSubmiting, setIsSubmiting] = useState(false);

  const defaultValue = useMemo(() => {
    if (
        statusDomain !== MultiDomainPersonalizationStatusEnum.NaoConfigurado && 
        statusDomain !== MultiDomainPersonalizationStatusEnum.Padrao && 
        domain.DomainName
      ) {
      const splitArray = domain.DomainName.split('.');
      const subdomainValue = splitArray.shift();
      const domainValue = splitArray.join('.');
      return {
        subdomain: subdomainValue,
        domain: domainValue,
      }
    }

    return {
      subdomain: '',
      domain: '',
    }
  }, [statusDomain, domain.DomainName])

  const handleUpdate = (
    useTenantFk: number,
    updateData: ITenantPersonalizationUpdateData[]
  ) => {
    if (useTenantFk && updateData.length > 0) {
      const request: IUpdateTenantPersonalizationRequest = {
        TenantFk: useTenantFk,
        updateData,
      }
      dispatch(MultiDomainActions.updateTenantPersonalization(request));
      setIsSubmiting(true);
    }
  }

  const handleSubmit = (values: any) => {
    if (tenantFk) {
      const updateData: ITenantPersonalizationUpdateData[] = [];

      if (
        values.subdomain && 
        typeof values.subdomain === 'string' &&
        values.domain &&
        typeof values.domain === 'string'
      ) {
        const newValue = `${values.subdomain}.${values.domain}`;
        updateData.push({
          path: "DomainName",
          op: 'replace',
          value: newValue,
        });
        updateData.push({
          path: "Status",
          op: 'replace',
          value: MultiDomainPersonalizationStatusEnum.ConfiguracaoEmAndamento,
        });
      }

      handleUpdate(tenantFk, updateData);
    }
  };

  const handleDNSConfirm = () => {
    if (tenantFk) {
      const request: ICheckDNSRequest = {
        tenantFk: tenantFk,
      }
      dispatch(MultiDomainActions.checkDNS(request));
      setIsSubmiting(true);
    }
  };

  const handleUpdateCancel = () => {
    setShowActionButtons(false);
  };

  useEffect(() => {
    if (isSubmiting && !loadingDomain) {
      setIsSubmiting(false);
      setShowActionButtons(false);
    }
  }, [loadingDomain, isSubmiting]);

  useEffect(() => {
    if (domain.Status) {
      setStatusDomain(domain.Status);
    }
  }, [domain.Status]);

  const initConfig = MultiDomainPersonalizationStatusEnum.NaoConfigurado === statusDomain || MultiDomainPersonalizationStatusEnum.Padrao === statusDomain;
  const DNSWating = MultiDomainPersonalizationStatusEnum.ConfiguracaoEmAndamento === statusDomain;
  const internalWating = MultiDomainPersonalizationStatusEnum.AguardandoAnaliseInterna === statusDomain;
  const OK = MultiDomainPersonalizationStatusEnum.ConfiguracaoConcluida === statusDomain;
  const DNSError = MultiDomainPersonalizationStatusEnum.ErroConfiguracao === statusDomain;

  const subDomainValue = (domain.DomainName && domain.DomainName.includes('.')) 
    ? domain.DomainName.split('.')[0] 
    : null;

  return (
    <>
      <MainWrapperLeft showInfo="true">
        <div className="cardHeader">
          <CardTitle>{t('customDomain.connectTitle')}</CardTitle>
          <CardSubTitle>
            {t('customDomain.connectSubtitle')}
          </CardSubTitle>
          <AntDivider />
          {(initConfig || showActionButtons) && (
            <>
              <CardSubTitle isBold="true" noMargin="true">
                {t('customDomain.addDomainLabel')}
              </CardSubTitle>
              <InputDNSWrapper showActionButton={`${showActionButtons}`}>
                <Form 
                  className="formWrapper" 
                  schema={schema} 
                  onSubmit={handleSubmit}
                  initialValues={defaultValue}
                >
                  <div className="formHeader">
                    <span className="infoText">https://</span>
                    <Field
                      name="subdomain"
                      label=""
                      placeholder={t('customDomain.subdomainPlaceholder')}
                      className="firstInput"
                    />
                    <span className="dot">.</span>
                    <Field
                      name="domain"
                      label=""
                      placeholder={t('customDomain.domainPlaceholder')}
                      className="lastInput"
                    />
                    {initConfig && (
                      <Button
                        type="primary"
                        loading={loadingDomain}
                        htmlType="submit"
                      >
                        {loadingDomain ? t('customDomain.saving') : t('customDomain.save')}
                      </Button>
                    )}
                  </div>
                  <div className="formFooter">
                    <CardSubTitle noMargin="true">
                      {t('customDomain.domainNote')}
                    </CardSubTitle>
                    {showActionButtons && (
                      <>
                        <AntDivider />
                        <ButtonsAction
                          isSubmit
                          loading={loadingDomain}
                          onCancel={handleUpdateCancel}
                        />
                      </>
                    )}
                  </div>
                </Form>
              </InputDNSWrapper>
            </>
          )}
          {!showActionButtons && !initConfig && (
            <DNSWrapper isInternal={`${internalWating}`} isOK={`${OK}`}>
              {OK ? (
                <Icon
                  icon="confirmacao"
                  className="dnsConfirmIcon"
                  customSize={24}
                />
              ) : (
                <Icon
                  icon="relogio"
                  className="dnsRelogioIcon"
                  customSize={24}
                />
              )}
              <span className="infoWrapper">
                <span className="info">
                  {DNSWating && t('customDomain.statusWaitingCNAME')}
                  {internalWating && t('customDomain.statusWaitingInternal')}
                  {OK && t('customDomain.statusOnline')}
                </span>
                <span className="dns">
                  https://
                  <span className="dnsBold">{domain.DomainName}</span>
                  /
                </span>
              </span>
              {(DNSWating || DNSError) && (
                <>
                  <Button 
                    type="text"
                    className="iconBtn"
                  >
                    <Icon
                      icon="editar"
                      className="dnsEditIcon"
                      customSize={10}
                      onClick={() => setShowActionButtons(true)}
                    />
                  </Button>
                  <Button
                    type="primary"
                    loading={loadingDomain}
                    className="cnameBtn"
                    onClick={handleDNSConfirm}
                  >
                    {loadingDomain ? t('customDomain.verifying') : t('customDomain.verifyCNAME')}
                  </Button>
                </>
              )}
              {(internalWating || OK) && (
                <Button
                  type="text"
                  className="internalBtn"
                  onClick={() => setShowActionButtons(true)}
                >
                  {t('customDomain.change')}
                </Button>
              )}
            </DNSWrapper>
          )}
          {(!initConfig) && (
            <>
              {!showActionButtons && (internalWating || DNSError) && (
                <InfoWrapper>
                  {internalWating && (
                    <span className="infoInternal">
                      {t('customDomain.internalWaitingInfo')} <span className="infoInternalBold">{t('customDomain.internalWaitingInfoBold')}</span>
                    </span>
                  )}
                  {DNSError && (
                    <span className="infoError">
                      {t('customDomain.dnsError')}
                    </span>
                  )}
                </InfoWrapper>
              )}
            </>
          )}
        </div>

        {(DNSWating || DNSError) && (
          <div className="cardMain">
            <span className="title">{t('customDomain.cnameSectionTitle')}</span>
            <span className="subTitle first">
              {t('customDomain.cnameGuideText')} <span className="subLink">{t('customDomain.cnameGuideLink')}</span>
            </span>
            <span className="subTitle">
              {t('customDomain.dnsPropagationNote')}
            </span>

            <div className="tableWrapper">
              <table>
                <thead>
                  <tr>
                    <th>{t('customDomain.tableHeaderType')}</th>
                    <th>{t('customDomain.tableHeaderName')}</th>
                    <th>{t('customDomain.tableHeaderDestination')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>CNAME</td>
                    <td>{subDomainValue ? subDomainValue : 'projetos'}</td>
                    <td>prd-web-maleta.azurewebsites.net</td>
                  </tr>
                  <tr>
                    <td>TXT</td>
                    <td>asuid.{subDomainValue ? subDomainValue : 'projetos'}</td>
                    <td>93D1C859615BF2A3162A195CFB33806C90740D4E02A0EE09F35F2850F6BD3766</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <span className="subTitle">
              {t('customDomain.contactSupportText')} <span className="subBold">{t('customDomain.contactSupportBold')}</span>
            </span>
          </div>
        )}
      </MainWrapperLeft>

      <Divider />

      <MainWrapperRight> 
        <div className="fixedContainer">
          <SvgCard hasCustom={`${!!((internalWating || OK) && domain.DomainName)}`}>
            <img src={CustomDomainPreview} />
            <span className="overlap">
              {internalWating && (
                <Icon
                  icon="relogio"
                  className="overlapRelogioIcon"
                  customSize={12}
                />
              )}
              {OK && (
                <Icon 
                  icon="cadeado"
                  className="overlapCadeadoIcon"
                  customSize={12}
                />
              )}
              {((internalWating || OK) && domain.DomainName) 
                ? domain.DomainName
                : ''
              }
            </span>
          </SvgCard>
        </div>
      </MainWrapperRight>
    </>
  )
}

export default CustomDomainTab;
